import React, { useCallback, useEffect, useRef, useState } from 'react';
import Modal from '../Modal';
import Button from '../Button';
import { FiLoader, FiMaximize2, FiNavigation } from 'react-icons/fi';
import Icon from '../Icon';
import { KARACHI_CENTER, loadGoogleMapsScript } from '../../utils/googleMapsLoader';

export type MapRouteOrigin = { lat: number; lng: number };

type Props = {
    isOpen: boolean;
    onClose: () => void;
    apiKey: string | undefined;
    /** Current form values — used when modal opens */
    latitude: string;
    longitude: string;
    onSubmit: (lat: number, lng: number) => void;
    /** Region anchor (locations collection). Shows start marker and driving route to the pin. */
    routeOrigin?: MapRouteOrigin | null;
};

function parseCoord(s: string, fallback: number): number {
    const n = parseFloat(String(s).replace(/,/g, '').trim());
    return Number.isNaN(n) ? fallback : n;
}

type RouteInfo = {
    /** Driving distance along roads when Directions succeeds */
    distanceMeters: number;
    durationSeconds: number;
    /** Google-formatted strings */
    distanceText: string;
    durationText: string;
};

export default function BranchMapPickerModal({
    isOpen,
    onClose,
    apiKey,
    latitude,
    longitude,
    onSubmit,
    routeOrigin = null,
}: Props) {
    const mapElRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const mapRef = useRef<google.maps.Map | null>(null);
    const markerRef = useRef<google.maps.Marker | null>(null);
    const originMarkerRef = useRef<google.maps.Marker | null>(null);
    const directionsRendererRef = useRef<google.maps.DirectionsRenderer | null>(null);
    const directionsServiceRef = useRef<google.maps.DirectionsService | null>(null);
    const routeDebounceRef = useRef<number | null>(null);
    const routeOriginRef = useRef<MapRouteOrigin | null>(null);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [preview, setPreview] = useState<{ lat: number; lng: number } | null>(null);
    const [geoBusy, setGeoBusy] = useState(false);
    const [routeInfo, setRouteInfo] = useState<RouteInfo | null>(null);
    const [routeLoading, setRouteLoading] = useState(false);

    const syncPreviewFromMarker = useCallback(() => {
        const m = markerRef.current?.getPosition();
        if (m) setPreview({ lat: m.lat(), lng: m.lng() });
    }, []);

    const clearRouteTimer = () => {
        if (routeDebounceRef.current) {
            clearTimeout(routeDebounceRef.current);
            routeDebounceRef.current = null;
        }
    };

    const requestDrivingRoute = useCallback(
        (origin: MapRouteOrigin, dest: google.maps.LatLng) => {
            const ds = directionsServiceRef.current;
            const dr = directionsRendererRef.current;
            if (!ds || !dr) return;

            setRouteLoading(true);
            ds.route(
                {
                    origin: { lat: origin.lat, lng: origin.lng },
                    destination: dest,
                    travelMode: google.maps.TravelMode.DRIVING,
                },
                (result, status) => {
                    setRouteLoading(false);
                    if (status === google.maps.DirectionsStatus.OK && result?.routes?.[0]?.legs?.[0]) {
                        const leg = result.routes[0].legs[0];
                        dr.setDirections(result);
                        setRouteInfo({
                            distanceMeters: leg.distance?.value ?? 0,
                            durationSeconds: leg.duration?.value ?? 0,
                            distanceText: leg.distance?.text ?? '',
                            durationText: leg.duration?.text ?? '',
                        });
                        return;
                    }

                    try {
                        // Runtime accepts empty routes to clear the polyline.
                        dr.setDirections({ routes: [] } as any);
                    } catch {
                        /* ignore clear errors */
                    }
                    setRouteInfo({
                        distanceMeters: 0,
                        durationSeconds: 0,
                        distanceText: '',
                        durationText: '',
                    });
                }
            );
        },
        []
    );

    const scheduleRouteUpdate = useCallback(
        (origin: MapRouteOrigin) => {
            const pos = markerRef.current?.getPosition();
            if (!pos) return;
            clearRouteTimer();
            routeDebounceRef.current = window.setTimeout(() => {
                requestDrivingRoute(origin, pos);
            }, 280);
        },
        [requestDrivingRoute]
    );
    const fitRouteBounds = useCallback((origin: MapRouteOrigin) => {
        const map = mapRef.current;
        const marker = markerRef.current;
        const pos = marker?.getPosition();
        if (!map || !pos) return;
        const bounds = new google.maps.LatLngBounds();
        bounds.extend({ lat: origin.lat, lng: origin.lng });
        bounds.extend({ lat: pos.lat(), lng: pos.lng() });
        map.fitBounds(bounds, { top: 56, right: 48, bottom: 56, left: 48 });
    }, []);

    const originKey = routeOrigin ? `${routeOrigin.lat.toFixed(6)},${routeOrigin.lng.toFixed(6)}` : '';

    useEffect(() => {
        routeOriginRef.current = routeOrigin;
    }, [routeOrigin]);

    useEffect(() => {
        if (!isOpen) {
            mapRef.current = null;
            markerRef.current = null;
            originMarkerRef.current = null;
            directionsRendererRef.current = null;
            directionsServiceRef.current = null;
            clearRouteTimer();
            setPreview(null);
            setError(null);
            setRouteInfo(null);
            setRouteLoading(false);
            return;
        }

        const key = apiKey?.trim();
        if (!key) {
            setError('Add REACT_APP_GOOGLE_MAPS_API_KEY to use the map.');
            return;
        }

        let cancelled = false;
        setLoading(true);
        setError(null);
        setRouteInfo(null);

        const lat0 = parseCoord(latitude, KARACHI_CENTER.lat);
        const lng0 = parseCoord(longitude, KARACHI_CENTER.lng);
        const hasOrigin = routeOrigin != null && Number.isFinite(routeOrigin.lat) && Number.isFinite(routeOrigin.lng);

        void (async () => {
            try {
                await loadGoogleMapsScript(key);
                if (cancelled || !mapElRef.current) return;

                const map = new google.maps.Map(mapElRef.current, {
                    center: { lat: lat0, lng: lng0 },
                    zoom: 14,
                    mapTypeControl: true,
                    streetViewControl: false,
                    fullscreenControl: true,
                });
                mapRef.current = map;

                directionsServiceRef.current = new google.maps.DirectionsService();
                const dr = new google.maps.DirectionsRenderer({
                    map,
                    suppressMarkers: true,
                    preserveViewport: true,
                    polylineOptions: {
                        strokeColor: '#2563eb',
                        strokeOpacity: 0.92,
                        strokeWeight: 5,
                    },
                });
                directionsRendererRef.current = dr;

                if (hasOrigin && routeOrigin) {
                    originMarkerRef.current = new google.maps.Marker({
                        position: { lat: routeOrigin.lat, lng: routeOrigin.lng },
                        map,
                        draggable: false,
                        title: 'Region (start)',
                        label: { text: 'A', color: 'white', fontWeight: 'bold' },
                        icon: {
                            path: google.maps.SymbolPath.CIRCLE,
                            scale: 10,
                            fillColor: '#16a34a',
                            fillOpacity: 1,
                            strokeColor: '#ffffff',
                            strokeWeight: 2,
                        },
                    });

                    const marker = new google.maps.Marker({
                        position: { lat: lat0, lng: lng0 },
                        map,
                        draggable: true,
                        animation: google.maps.Animation.DROP,
                        title: 'Branch (drop)',
                        label: { text: 'B', color: 'white', fontWeight: 'bold' },
                        icon: {
                            path: google.maps.SymbolPath.CIRCLE,
                            scale: 10,
                            fillColor: '#dc2626',
                            fillOpacity: 1,
                            strokeColor: '#ffffff',
                            strokeWeight: 2,
                        },
                    });
                    markerRef.current = marker;
                    setPreview({ lat: lat0, lng: lng0 });

                    fitRouteBounds(routeOrigin);

                    requestDrivingRoute(routeOrigin, marker.getPosition()!);

                    map.addListener('click', (e: google.maps.MapMouseEvent) => {
                        if (e.latLng) {
                            marker.setPosition(e.latLng);
                            setPreview({ lat: e.latLng.lat(), lng: e.latLng.lng() });
                            scheduleRouteUpdate(routeOrigin);
                        }
                    });
                    marker.addListener('dragend', () => {
                        syncPreviewFromMarker();
                        scheduleRouteUpdate(routeOrigin);
                    });
                } else {
                    originMarkerRef.current = null;
                    const marker = new google.maps.Marker({
                        position: { lat: lat0, lng: lng0 },
                        map,
                        draggable: true,
                        animation: google.maps.Animation.DROP,
                    });
                    markerRef.current = marker;
                    setPreview({ lat: lat0, lng: lng0 });

                    map.addListener('click', (e: google.maps.MapMouseEvent) => {
                        if (e.latLng) {
                            marker.setPosition(e.latLng);
                            setPreview({ lat: e.latLng.lat(), lng: e.latLng.lng() });
                        }
                    });
                    marker.addListener('dragend', () => syncPreviewFromMarker());
                }

                window.setTimeout(() => {
                    if (cancelled || !searchInputRef.current) return;
                    const ac = new google.maps.places.Autocomplete(searchInputRef.current, {
                        componentRestrictions: { country: 'pk' },
                        fields: ['geometry', 'formatted_address', 'name', 'address_components'],
                    });
                    ac.bindTo('bounds', map);
                    ac.addListener('place_changed', () => {
                        const place = ac.getPlace();
                        const loc = place.geometry?.location;
                        if (loc && markerRef.current) {
                            map.setCenter(loc);
                            map.setZoom(16);
                            markerRef.current.setPosition(loc);
                            setPreview({ lat: loc.lat(), lng: loc.lng() });
                            const o = routeOriginRef.current;
                            if (o) scheduleRouteUpdate(o);
                        }
                    });
                }, 0);
            } catch (e: unknown) {
                if (!cancelled) {
                    setError(e instanceof Error ? e.message : 'Could not load map');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
            clearRouteTimer();
            directionsRendererRef.current?.setMap(null);
            directionsRendererRef.current = null;
            directionsServiceRef.current = null;
            originMarkerRef.current = null;
            markerRef.current = null;
            mapRef.current = null;
            if (mapElRef.current) mapElRef.current.innerHTML = '';
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- remount map when opening or when anchor/coords identity changes
    }, [isOpen, apiKey, latitude, longitude, originKey, syncPreviewFromMarker, scheduleRouteUpdate, requestDrivingRoute, fitRouteBounds]);

    const handleMyLocation = () => {
        if (!navigator.geolocation) {
            setError('Geolocation is not supported in this browser.');
            return;
        }
        setGeoBusy(true);
        setError(null);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setGeoBusy(false);
                const lat = pos.coords.latitude;
                const lng = pos.coords.longitude;
                const map = mapRef.current;
                const marker = markerRef.current;
                if (map && marker) {
                    const loc = new google.maps.LatLng(lat, lng);
                    map.setCenter(loc);
                    map.setZoom(16);
                    marker.setPosition(loc);
                    setPreview({ lat, lng });
                    const o = routeOriginRef.current;
                    if (o) scheduleRouteUpdate(o);
                }
            },
            () => {
                setGeoBusy(false);
                setError('Could not get your location. Allow location access or drag the pin.');
            },
            { enableHighAccuracy: true, timeout: 12000 }
        );
    };

    const handleApply = () => {
        const m = markerRef.current?.getPosition();
        if (!m) return;
        onSubmit(m.lat(), m.lng());
        onClose();
    };

    const hasOrigin = Boolean(routeOrigin);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={hasOrigin ? 'Pick branch on map (from region)' : 'Pick location on map'}
            size="lg"
            overlayClassName="!z-[60]"
            bodyClassName="flex min-h-[28rem] flex-1 flex-col overflow-hidden bg-white dark:bg-gray-800"
            closeOnBackdropClick={false}
        >
            <div className="flex min-h-0 flex-1 flex-col">
                {!apiKey?.trim() ? (
                    <p className="p-4 text-sm text-red-600 dark:text-red-400">{error}</p>
                ) : (
                    <>
                        <div className="shrink-0 space-y-2 border-b border-gray-200 p-4 dark:border-gray-700">
                            {hasOrigin && routeOrigin && (
                                <div className="flex flex-wrap items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50/90 px-3 py-2 text-xs dark:border-emerald-900/50 dark:bg-emerald-950/40">
                                    <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-[11px] font-bold text-white">
                                        A
                                    </span>
                                    <span className="text-emerald-900 dark:text-emerald-100">
                                        <span className="font-semibold">Start (region)</span>
                                        <span className="ml-2 font-mono text-emerald-800/90 dark:text-emerald-200/90">
                                            {routeOrigin.lat.toFixed(5)}, {routeOrigin.lng.toFixed(5)}
                                        </span>
                                    </span>
                                </div>
                            )}
                            <label className="form-field-label">Search (Pakistan)</label>
                            <input
                                ref={searchInputRef}
                                type="text"
                                placeholder="Search address or postal area…"
                                className="form-control text-sm"
                                autoComplete="off"
                            />
                            <div className="flex flex-wrap gap-2">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    className="!py-2"
                                    disabled={geoBusy || loading}
                                    onClick={handleMyLocation}
                                >
                                    <span className="inline-flex items-center gap-2">
                                        {geoBusy ? (
                                            <Icon icon={FiLoader} className="h-4 w-4 animate-spin" />
                                        ) : (
                                            <Icon icon={FiNavigation} className="h-4 w-4" />
                                        )}
                                        My location
                                    </span>
                                </Button>
                                <span className="self-center text-xs text-gray-500 dark:text-gray-400">
                                    {hasOrigin
                                        ? 'Red pin B = branch — driving route from green A'
                                        : 'Drag pin or click map — Karachi default'}
                                </span>
                            </div>
                        </div>

                        {hasOrigin && (
                            <div className="shrink-0 border-b border-gray-200 bg-slate-50 px-4 py-3 dark:border-gray-700 dark:bg-slate-900/50">
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                                    <span className="inline-flex items-center gap-1.5 font-medium text-slate-800 dark:text-slate-100">
                                        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-[10px] font-bold text-white">
                                            B
                                        </span>
                                        Drop point (branch)
                                    </span>
                                    {routeLoading && (
                                        <span className="inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                                            <Icon icon={FiLoader} className="h-3.5 w-3.5 animate-spin" />
                                            Routing…
                                        </span>
                                    )}
                                    <span className="text-slate-600 dark:text-slate-300">
                                        <span className="font-semibold">Total distance</span>
                                        <span className="ml-1.5 tabular-nums text-slate-900 dark:text-slate-100">
                                            {routeInfo && routeInfo.distanceMeters > 0
                                                ? `${(routeInfo.distanceMeters / 1000).toFixed(1)} km`
                                                : '—'}
                                        </span>
                                    </span>
                                    <span className="text-slate-600 dark:text-slate-300">
                                        <span className="font-semibold">ETA (car)</span>
                                        <span className="ml-1.5 tabular-nums text-slate-900 dark:text-slate-100">
                                            {routeInfo && routeInfo.durationSeconds > 0
                                                ? routeInfo.durationText || `${Math.ceil(routeInfo.durationSeconds / 60)} min`
                                                : '—'}
                                        </span>
                                    </span>
                                </div>
                            </div>
                        )}

                        <div className="relative min-h-[320px] flex-1 bg-gray-100 dark:bg-gray-900">
                            {loading && (
                                <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 dark:bg-black/40">
                                    <Icon icon={FiLoader} className="h-8 w-8 animate-spin text-gray-500" />
                                </div>
                            )}
                            {error && !loading && (
                                <div className="absolute inset-x-0 top-0 z-10 bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/50 dark:text-red-300">
                                    {error}
                                </div>
                            )}
                            {hasOrigin && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        const o = routeOriginRef.current;
                                        if (o) fitRouteBounds(o);
                                    }}
                                    className="absolute right-3 top-3 z-10 inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-300 bg-white/95 text-gray-700 shadow-sm transition hover:bg-white dark:border-gray-600 dark:bg-gray-900/90 dark:text-gray-200"
                                    title="Show full route"
                                    aria-label="Show full route"
                                >
                                    <Icon icon={FiMaximize2} className="h-4 w-4" />
                                </button>
                            )}
                            <div ref={mapElRef} className="h-full min-h-[320px] w-full" />
                        </div>

                        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-gray-200 p-4 dark:border-gray-700">
                            <div className="font-mono text-sm text-gray-700 dark:text-gray-300">
                                {preview ? (
                                    <>
                                        <span className="text-gray-500 dark:text-gray-400">Lat </span>
                                        {preview.lat.toFixed(6)}
                                        <span className="mx-2 text-gray-400">·</span>
                                        <span className="text-gray-500 dark:text-gray-400">Long </span>
                                        {preview.lng.toFixed(6)}
                                    </>
                                ) : (
                                    '—'
                                )}
                            </div>
                            <div className="flex gap-2">
                                <Button type="button" variant="secondary" className="!py-2" onClick={onClose}>
                                    Cancel
                                </Button>
                                <Button type="button" className="!py-2" onClick={handleApply} disabled={loading || !preview}>
                                    Apply coordinates
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
}
